import { ascend, compose, findIndex, prop, propEq, sortWith } from 'ramda';

import MOVE_TYPES from '../enums/moveTypes';
import RouteStatus from '../enums/RouteStatus';

const sortBy = (property) => ascend(prop(property));

export const getSortedStops = (stops = []) =>
  sortWith([
    ascend(compose(Number.parseInt, prop('sequenceNumber'))),
    sortBy('displayNumber'),
  ])(stops);

export const updateSeqAndDisplayNumbers = (stops, shouldDisplayNumberUpdate) =>
  stops.map((stop, idx) => ({
    ...stop,
    sequenceNumber: idx + 1,
    displayNumber: shouldDisplayNumberUpdate
      ? `${idx + 1}`
      : stop.displayNumber,
  }));

export const removeSelectedStops = (stops, selectedStops) => {
  const selectedStopsSet = new Set(
    selectedStops.map(({ stopNumber }) => stopNumber)
  );

  return stops.filter(({ stopNumber }) => !selectedStopsSet.has(stopNumber));
};

export const isStopInSelectedStops = (stop, selectedStops) =>
  selectedStops.some((s) => s.stopNumber === stop.stopNumber);

export const replaceMergeTargetAndRemoveSelected = (
  stops,
  selectedStops,
  newStop
) =>
  stops.reduce((acc, stop) => {
    if (stop.stopNumber === newStop.stopNumber) {
      acc.push(newStop);
    } else if (!isStopInSelectedStops(stop, selectedStops)) {
      acc.push(stop);
    }
    return acc;
  }, []);

export const getIndexAboveSelected = (
  stops,
  selectedStops,
  separatorStopNumber
) => {
  const index = findIndex(
    propEq(
      separatorStopNumber ? separatorStopNumber : selectedStops[0].stopNumber,
      'stopNumber'
    )
  )(stops);

  if (separatorStopNumber) {
    const selectedStopsNumbers = selectedStops.map(
      ({ stopNumber }) => stopNumber
    );
    const stopsBeforeTarget = stops
      .slice(0, index)
      .filter(({ stopNumber }) =>
        selectedStopsNumbers.includes(stopNumber)
      ).length;

    return index - stopsBeforeTarget;
  }

  return index - 1;
};

export const updateSelectedStops = (stops, separator, isLoading) =>
  stops.map((stop) => ({
    ...stop,
    selected: false,
    displayNumber: isLoading
      ? `${separator ? separator.sequenceNumber : 1}.${stop.stopNumber}`
      : stop.displayNumber,
  }));

export const move = (
  stops,
  selectedStops,
  topSeparatorIndex,
  isLoading,
  isMoveDown
) => {
  let updatedStops;

  if (isMoveDown) {
    updatedStops = [
      ...stops.slice(0, topSeparatorIndex + 2),
      ...updateSelectedStops(
        selectedStops,
        stops[topSeparatorIndex + 1],
        isLoading
      ),
      ...stops.slice(topSeparatorIndex + 2),
    ];
  } else {
    const isMoveAboveFirst = topSeparatorIndex === -1;
    updatedStops = [
      ...stops.slice(0, isMoveAboveFirst ? 0 : topSeparatorIndex),
      ...updateSelectedStops(
        selectedStops,
        stops[isMoveAboveFirst ? 0 : topSeparatorIndex - 1],
        isLoading
      ),
      ...stops.slice(isMoveAboveFirst ? 0 : topSeparatorIndex),
    ];
  }

  return updateSeqAndDisplayNumbers(updatedStops, !isLoading);
};

export const moveStops = ({
  stop,
  status,
  direction,
  selectedStops,
  separatorStopNumber,
}) => {
  const isLoading = status === RouteStatus.LOADING;
  const isMoveDown = direction === MOVE_TYPES.DOWN;
  const sortedStops = getSortedStops(stop);
  const sortedSelectedStops = getSortedStops(selectedStops);
  const stopsWithNewSeq = updateSeqAndDisplayNumbers(sortedStops);
  const untouchableStops = removeSelectedStops(stopsWithNewSeq, selectedStops);
  const topSeparatorIndex = getIndexAboveSelected(
    stopsWithNewSeq,
    sortedSelectedStops,
    separatorStopNumber
  );

  return move(
    untouchableStops,
    sortedSelectedStops,
    topSeparatorIndex,
    isLoading,
    isMoveDown
  );
};
